/**
 * Accept the strategist's targeting and continue the pipeline.
 *
 * Shows exactly what the strategist will steer (its article_direction, fed into
 * the brief + drafting), and an "Accept & continue" action that runs the
 * pipeline forward from the stage after the strategist — skipping the manual
 * keyword/SERP research stages the strategist replaces. WordPress publish is
 * left for the human.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { blogRunsService } from '../../../services/blogRunsService';
import { jobsService } from '../../../services/jobsService';
import { useArticleContext } from '../ArticleContext';

export function StrategistAcceptBar({
  verdict,
  articleDirection,
}: {
  verdict?: 'proceed' | 'reject';
  articleDirection?: string | null;
}) {
  const { run, refreshRun, refreshDocument, refreshArticles } = useArticleContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const accept = useCallback(async () => {
    if (!run?.run_id || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { job_id } = await blogRunsService.acceptTargeting(run.run_id);
      await refreshRun();
      // The pipeline runs many stages; poll + refresh so the sidebar advances.
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        let job;
        try {
          job = await jobsService.get(job_id);
        } catch {
          continue;
        }
        await refreshRun();
        await refreshDocument();
        if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
          await refreshArticles();
          if (job.status !== 'succeeded' && mounted.current) {
            setError('The pipeline stopped before finishing — check the stage sidebar for where.');
          }
          break;
        }
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : 'Failed to continue');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [run?.run_id, busy, refreshRun, refreshDocument, refreshArticles]);

  if (verdict !== 'proceed') return null;

  return (
    <div className="border border-green-300 bg-green-50 rounded-lg p-3 space-y-2">
      <div className="text-xs font-semibold text-green-800">Ready to build</div>

      {articleDirection && (
        <div>
          <div className="text-[11px] font-medium text-green-700 mb-0.5">
            Direction the strategist will steer the article with:
          </div>
          <div className="text-xs text-gray-700 bg-white border border-green-100 rounded px-2 py-1.5 whitespace-pre-wrap">
            {articleDirection}
          </div>
          <div className="text-[10px] text-green-600 mt-1">→ fed into the brief + drafting stages.</div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => void accept()}
          disabled={busy}
          className="text-sm px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 font-medium"
        >
          {busy ? 'Building…' : 'Accept & continue →'}
        </button>
        <span className="text-[11px] text-green-700">
          Skips the manual keyword/SERP steps; builds through the draft (publish stays with you).
        </span>
      </div>

      {busy && (
        <div className="text-xs text-green-700">Running the pipeline… watch progress in the stage sidebar.</div>
      )}
    </div>
  );
}
